import { z } from "zod";
import { CHANNELS, LIMITS } from "../constants";
import { DELIVERY_ISSUES } from "../issues";
import { IsoDate, Uuid } from "./common";
import { TimeZone } from "./customers";

export const OverviewQuery = z.object({
  app_id: Uuid.optional(),
  days: z.coerce.number().int().min(1).max(LIMITS.statsMaxDays).default(7),
  tz: TimeZone.default("UTC").meta({ description: "IANA time zone used for days and hours." }),
  include_test: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional()
    .meta({ description: "Include messages sent with test API keys. Default false." }),
});
export type OverviewQuery = z.input<typeof OverviewQuery>;

const OutcomeCounts = z.object({
  total: z.int(),
  delivered: z.int().meta({ description: "Confirmed delivered by the provider." }),
  accepted: z.int().meta({ description: "Accepted by the provider without a delivery receipt (push)." }),
  problems: z.int().meta({ description: "Failed, skipped or bounced." }),
  in_progress: z.int(),
});

export const Overview = z
  .object({
    range: z.object({ from: IsoDate, to: IsoDate, days: z.int(), tz: z.string() }),
    messages: z.int(),
    totals: OutcomeCounts,
    previous: OutcomeCounts.meta({ description: "Same counts for the period before, for comparison." }),
    per_channel: z.array(OutcomeCounts.extend({ channel: z.enum(CHANNELS) })),
    daily: z.array(
      z.object({ date: z.string().meta({ example: "2026-10-16" }), total: z.int(), problems: z.int() }),
    ),
    heatmap: z
      .object({
        total: z.array(z.array(z.int())),
        problems: z.array(z.array(z.int())),
      })
      .meta({
        description: "7 x 24 matrices of deliveries by weekday (0 = Monday) and hour of day, in `tz`.",
      }),
    issues: z.array(z.object({ issue: z.enum(DELIVERY_ISSUES), count: z.int() })).meta({
      description: "Problem kinds, most frequent first.",
    }),
    customers: z.object({ total: z.int(), new: z.int() }),
  })
  .meta({ id: "Overview" });
export type Overview = z.infer<typeof Overview>;

// ---------------------------------------------------------------------------
// Customer map (D-43)
// ---------------------------------------------------------------------------

export const GEO_PRECISIONS = ["exact", "city", "region"] as const;
export type GeoPrecision = (typeof GEO_PRECISIONS)[number];

export const GeoQuery = z.object({
  app_id: Uuid.optional(),
  days: z.coerce.number().int().min(1).max(LIMITS.statsMaxDays).default(7),
  include_test: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
});

const GeoCounts = {
  customers: z.int(),
  deliveries: z.int().meta({ description: "Deliveries to these customers in the period." }),
  problems: z.int().meta({ description: "Of those, failed, skipped or bounced." }),
};

export const GeoOverview = z
  .object({
    range: z.object({ from: IsoDate, to: IsoDate, days: z.int() }),
    points: z
      .array(
        z.object({
          lat: z.number(),
          lng: z.number(),
          precision: z.enum(GEO_PRECISIONS).meta({
            description:
              "`exact`: coordinates sent by the client, rounded to about 1 km. `city` or `region`: placed at the city centre or province capital.",
          }),
          label: z.string().nullable(),
          ...GeoCounts,
        }),
      )
      .meta({ description: "Customers aggregated per location. Individual coordinates are never returned." }),
    areas: z
      .array(
        z.object({
          city: z.string().nullable(),
          region: z.string().nullable(),
          country: z.string().nullable(),
          ...GeoCounts,
        }),
      )
      .meta({ description: "Top areas by customers, the table view of the map." }),
    totals: z.object({
      customers: z.int(),
      exact: z.int(),
      approximate: z.int(),
      unlocated: z.int(),
    }),
  })
  .meta({ id: "GeoOverview" });
export type GeoOverview = z.infer<typeof GeoOverview>;
